import React from "react";
import { FormGroup, Label } from "reactstrap";

import { themeFieldsChanged } from "../../lib/themeFieldsChanged";
import { tooltips } from "../../../../../../lib/utils/tooltips";
import { InformationBox } from "../../../../InformationBox/InformationBox";
import { ThemeCard, ThemeType } from "./ThemeCard";
import { Input } from "../../../../Input/Input";
import { LabelWithInformationBox } from "../../../../LabelWithInformationBox/LabelWithInformationBox";
import { ThemeCustomColors } from "./ThemeCustomColors";
import { DisplaySettingsFormik } from "../../lib/useDisplaySettingsForm";
import { InputDescription } from "../../../../InputDescription/InputDescription";

import "./ThemeSettings.scss";

interface Props {
  formik: DisplaySettingsFormik;
  isBussinesOrPro?: boolean;
  isProfile?: boolean;
}

export const ThemeSettings = ({
  formik,
  isBussinesOrPro,
  isProfile,
}: Props) => {
  const { main } = formik.values;

  return (
    <div className="ThemeSettings">
      <ThemeCard formik={formik} isBussinesOrPro={isBussinesOrPro} />

      {isBussinesOrPro && (
        <FormGroup className="position-relative ThemeSettings__useColorImage mt-3">
          <Input
            type="checkbox"
            id="main.layoutTheme.useColorImage"
            name="main.layoutTheme.useColorImage"
            data-cy="main.layoutTheme.useColorImage"
            checked={main.layoutTheme.useColorImage ?? false}
            onChange={formik.handleChange}
          />
          <Label
            for="main.layoutTheme.useColorImage"
            className="checkbox_label"
          >
            <span>Use color background image (disable for greyscale)</span>
            <span className="checkbox_box"></span>
          </Label>
          <InformationBox message={tooltips.rooms.main.backgroundImage} />
        </FormGroup>
      )}

      {formik.values.main.layoutTheme.themeVersion === ThemeType.nivo && (
        <>
          <FormGroup className="ThemeSettings__backgroundOpacity">
            <LabelWithInformationBox
              title="Adjust background opacity to show status colors"
              for="main.layoutTheme.backgroundImageOpacity"
              message={tooltips.rooms.main.backgroundOpacity}
            />
            <span>Controls how much the status fill tints the image.</span>
            <span className="d-block mb-2">
              100% = image only, 50% = balanced, 0% = status only
            </span>

            <div className="ThemeSettings--bgopacity">
              <Input
                type="number"
                id="backgroundImageOpacity"
                name="main.layoutTheme.backgroundImageOpacity"
                min={0}
                max={100}
                value={main.layoutTheme.backgroundImageOpacity ?? 100}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                errorMessage={
                  formik.errors.main?.layoutTheme?.backgroundImageOpacity
                }
                invalid={
                  !!formik.errors.main?.layoutTheme?.backgroundImageOpacity &&
                  formik.touched.main?.layoutTheme?.backgroundImageOpacity
                }
                isTouched
                placeholder="100"
                className="text-center ThemeSettings__opacity mb-4"
              />
              {!formik.errors.main?.layoutTheme?.backgroundImageOpacity && (
                <span>%</span>
              )}
            </div>
          </FormGroup>

          <FormGroup>
            <LabelWithInformationBox
              title="Custom status colors (not recommended)"
              for="display.pendingTimeAfterMeeting"
              message={tooltips.rooms.main.hexColors}
            />

            <ThemeCustomColors formik={formik} />
          </FormGroup>
        </>
      )}

      {!isProfile &&
        themeFieldsChanged(formik.values, formik.initialValues) && (
          <InputDescription
            title="Click Save at the bottom to apply the theme settings."
            className="custom-font mt-0"
          />
        )}
    </div>
  );
};
